import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { HeroLine } from "@/components/Reveal";
import { IMG } from "@/data/site";
import { openCalendly } from "@/lib/calendly";

export const Hero = ({ onScrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative h-[100svh] w-full overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img src={IMG.hero} alt="Gold Media Studio Filmproduktion Hamburg" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#050505]" />
        <div className="absolute inset-0 bg-black/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-screen-2xl mx-auto px-6 sm:px-10 flex flex-col justify-end pb-24 sm:pb-28">
        <h1 className="font-serif font-light leading-[0.92] text-[13vw] sm:text-[10vw] lg:text-[8.5rem] tracking-tight uppercase">
          <HeroLine delay={0.35}>Wir schaffen Bilder,</HeroLine>
          <HeroLine delay={0.5} className="text-[#d4c4a8] italic">die bleiben.</HeroLine>
        </h1>

        <motion.div
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
        >
          <p className="font-display uppercase tracking-[0.25em] text-sm text-[#a1a1aa]">
            Produktion. Content. Werbung.
          </p>
          <div className="flex flex-wrap gap-4">
            <button data-testid="hero-cta-primary" onClick={() => onScrollTo("#kontakt")} className="btn-gold">
              Projekt starten
            </button>
            <button data-testid="hero-cta-secondary" onClick={openCalendly} className="btn-ghost">
              Termin buchen
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 right-8 z-10 hidden sm:flex items-center gap-3 text-[#71717a]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
      >
        <span className="font-display uppercase tracking-[0.25em] text-xs">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} strokeWidth={1.4} />
        </motion.span>
      </motion.div>
    </section>
  );
};
